/**
 * @file: index.tsx
 *
 * The mobile chat conversation PAGE — a navigator screen, used only via the
 * navigator (never rendered as a component; the desktop pane mounts
 * `ChatConversationView` directly). Its route param IS the
 * `ConversationIntent` the opener expressed; it adds the mobile chrome: the
 * navigator owns the header (`ChatHeaderTitle` + `ConversationHeaderRight`'s
 * `⋮`), so it passes no `onOpenRoomInfo` and mounts `<TypingIndicator/>` to
 * fold the typing line into the navigator title. When a first send
 * materialises the room, `onConversationHandleMaterialized` rewrites the route
 * param to a `resolved` intent so the header's `⋮` picks up the new room.
 */

import { ChatConversationView } from '@euler/app/flows/chat/components/conversation/ChatConversationView';
import { ChatHeaderTitle } from '@euler/app/flows/chat/components/conversation/ChatHeaderTitle';
import { ConversationHeaderRight } from '@euler/app/flows/chat/components/conversation/ConversationHeaderRight';
import { ConversationSkeleton } from '@euler/app/flows/chat/components/conversation/ConversationSkeleton';
import { TypingIndicator } from '@euler/app/flows/chat/components/conversation/TypingIndicator';
import {
  BakedConversationHandle,
  ChatConversationHandle,
  roomTitle,
} from '@euler/app/flows/chat/lib/conversation/handle';
import { ConversationIntent } from '@euler/app/flows/chat/lib/conversation/types';
import { useFloatingPanelClose } from '@euler/app/flows/chat/lib/floating/panelContext';
import { resolveConversationHandle } from '@euler/app/flows/chat/utils/resolveConversationHandle';
import { useAlert } from '@euler/functions/ux/useAlert';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { useServiceFactory } from '@euler/services/factory';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { FC, memo, useCallback, useEffect, useState } from 'react';

/** Initial static header title from the intent (TypingIndicator refines it). */
function intentTitle(intent: ConversationIntent | undefined): string {
  if (!intent) return '聊天';
  if (intent.kind === 'resolved') return roomTitle(intent.handle);
  if (intent.kind === 'contact') return intent.peer.name ?? '聊天';
  return '聊天';
}

const ChatConversationHost: FC<{ onResolveError?: () => void }> = memo(
  ({ onResolveError }) => {
    const route = useRoute();
    const navigation = useNavigation<StackNavigationProp<any>>();
    const { chatService } = useServiceFactory();
    const alert = useAlert();
    // Inside the floating panel's standalone stack this closes the panel; in
    // the app-level chat flow it's undefined and `goBack` bubbles out of the
    // flow's root screen, popping the whole flow off the root stack.
    const panelClose = useFloatingPanelClose();
    const rawIntent = route.params as unknown as ConversationIntent;

    // Resolve a pending intent (a push deep-link arrives as `{kind:'room'}`) at
    // the entry gate, BEFORE the conversation mounts: skeleton while resolving,
    // alert + go back on failure — a broken room never renders. An in-app open
    // already passes a `resolved` intent (the host resolved it), so it's instant.
    const [handle, setHandle] = useState<ChatConversationHandle | null>(() =>
      rawIntent?.kind === 'resolved' ? rawIntent.handle : null,
    );
    useEffect(() => {
      if (!rawIntent || rawIntent.kind === 'resolved') return;
      let cancelled = false;
      resolveConversationHandle(chatService, rawIntent)
        .then(resolved => {
          if (cancelled) return;
          setHandle(resolved);
          // Rewrite the route param to the resolved handle so the header's
          // `⋮` (which reads route params) can open the room detail.
          navigation.setParams({ kind: 'resolved', handle: resolved } as never);
        })
        .catch(async e => {
          console.error('resolve conversation failed', e);
          if (cancelled) return;
          // Alert first; once dismissed, let whoever rendered this host decide
          // what "go back" means (the navigator screen pops the pushed page).
          await alert({ title: '无法打开会话', message: '请稍后重试' });
          onResolveError?.();
        });
      return () => {
        cancelled = true;
      };
      // Resolve once on mount — the screen is pushed fresh per conversation.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onConversationHandleMaterialized = useCallback(
      (baked: BakedConversationHandle) => {
        setHandle(baked);
        navigation.setParams({ kind: 'resolved', handle: baked } as never);
      },
      [navigation],
    );

    // Floating-panel entry only: the inbox lists only rooms you're in, but the
    // order chat FAB can open a room you're NOT in yet. Redirect a non-member
    // to the room detail (which shows the "加入群聊" CTA) instead of an empty,
    // message-gated conversation. Scoped to the panel so member opens don't pay
    // the extra round-trip.
    const [memberChecked, setMemberChecked] = useState(panelClose == null);
    useEffect(() => {
      if (panelClose == null || !handle || handle.kind !== 'baked') {
        setMemberChecked(true);
        return;
      }
      let cancelled = false;
      chatService
        .getRoom(handle.roomId)
        .then(detail => {
          if (cancelled) return;
          if (detail.viewerIsMember === false) {
            navigation.replace('_chatRoomDetail' as never, handle as never);
          } else {
            setMemberChecked(true);
          }
        })
        .catch(() => {
          if (!cancelled) setMemberChecked(true);
        });
      return () => {
        cancelled = true;
      };
    }, [handle, panelClose, chatService, navigation]);

    if (!handle || !memberChecked) return <ConversationSkeleton />;
    return (
      <ChatConversationView
        handle={handle}
        onConversationHandleMaterialized={onConversationHandleMaterialized}
        // After deleting a closed thread from the banner: close the floating
        // panel if we're in it, else leave the chat flow (back to the inbox).
        onExit={panelClose ?? (() => navigation.goBack())}
      >
        <TypingIndicator />
      </ChatConversationView>
    );
  },
);

export const ChatConversationScreen = wrapNavigatorScreen(
  () => {
    // This screen is navigator-pushed, so a failed resolve goes back to where
    // the push/open came from. The host leaves the choice to its renderer; a
    // different renderer could pass nothing.
    const navigation = useNavigation();
    return <ChatConversationHost onResolveError={() => navigation.goBack()} />;
  },
  ({ route }: any) => ({
    title: intentTitle(route?.params as ConversationIntent | undefined),
    headerTitle: (props: any) => <ChatHeaderTitle {...props} />,
    headerRight: () => <ConversationHeaderRight route={route} />,
  }),
);
